import { jsPDF } from "jspdf";
import {
  formatTimecode, timecodeAt, timecodeSpec,
  type TcAnchor, type TimecodeSpec,
} from "../timecode/index.js";
import { erPickup } from "../synk/index.js";
import { reportStem, type ReportNaming } from "./filnavn.js";

/*
 * ========  RAPPORTEN ER DELT, OG MAATTE BLI DET  ========
 *
 * Fila laa i `apps/mobile/src/report/pdf.ts` til 02.11.2026. Da ba brukeren
 * om aa kunne avslutte dagen fra nettflata ogsaa: «slik at live loggen og
 * synken blir til en rapport.» To byggere for samme dokument er to
 * dokumenter — redaktoeren skal ikke kunne se hvilken flate rapporten ble
 * laget paa.
 *
 * INN-TYPENE ER DEFINERT HER, smalt og strukturelt: `state/types.ts` i appen
 * hoerer til appen, og delt kode skal ikke vite om den. Appens egne typer er
 * tilordnbare fordi feltnavnene er de samme — har de flere felt, er det
 * greit.
 */

/** Produksjonen, slik rapporten trenger den. */
export interface RapportProduksjon {
  readonly name: string;
}

/** Den som logget. */
export interface RapportMedlem {
  readonly displayName: string;
}

/**
 * Én loggpost.
 *
 * Avlesningen (`monotonicMs`, `bootId`, `wallUtcMs`) er det tidskoden regnes
 * av — posten baerer aldri en tidskode selv. Se CLAUDE.md.
 */
export interface RapportPost {
  readonly id: string;
  readonly label: string;
  readonly text: string;
  readonly monotonicMs: number;
  readonly bootId: string;
  readonly wallUtcMs: number;
  readonly tcState: string;
  readonly deletedAt: number | null;
}

export interface RapportDeltaker {
  readonly id: string;
  readonly navn: string;
  readonly forkortelse: string;
}

/** Hvor et spoersmaal kom fra, naar det ble skrevet ut av en loggpost. */
export interface RapportKilde {
  /** Posten spoersmaalet ble skrevet ut av. Binder de to i rapporten. */
  readonly entryId: string;
  readonly tcFrames: number;
  readonly framerate: string;
  readonly dropFrame: boolean;
  readonly opptaksdag: string;
  readonly tekst: string;
  readonly rapportversjon: number | null;
}

export interface RapportSpoersmaal {
  readonly id: string;
  readonly deltakerId: string | null;
  readonly tekst: string;
  readonly skrevetMs: number | null;
  readonly opptaksdag: string | null;
  readonly kilde?: RapportKilde | null;
}

export interface RapportSvar {
  readonly entryId: string;
  readonly spoersmaalId: string;
  readonly deltakerId: string;
  readonly svar: string;
}

/**
 * Et spoersmaal som er stilt ET ANNET STED i produksjonen.
 *
 * ========  «STILT AV LARS»  ========
 *
 * Brukeren 03.11.2026: «Om synkspoersmaalet er stilt av samme regi samme
 * opptaksdag, saa er det merket som stilt. Hvis det er noen andre som har
 * synket, men gjort det innen rapporten er levert, saa kommer det opp at
 * spoersmaalet er stilt av (for eksempel Lars). Hvis det ikke er stilt, saa
 * staar det at det ikke er stilt enda.»
 *
 * Et spoersmaal som er stilt ER STILT FOR ALLE — avgjort 25.10.2026 — men
 * rapporten er ÉN loggs dokument. Uten denne lista sto alt som ble stilt av
 * en annen som «ikke stilt», og redaktoeren ville bedt om det paa nytt.
 */
export interface RapportStiltEllers {
  readonly spoersmaalId: string;
  readonly deltakerId: string;
  /** Navnet paa den som stilte det. Tom naar det ikke er kjent. */
  readonly hvem: string;
  /** Opptaksdagen det ble stilt paa. Tom naar den ikke er kjent. */
  readonly opptaksdag: string;
}

export interface ReportInput {
  readonly production: RapportProduksjon;
  /** Hvem som logget. Laa foer paa produksjonen, men hoerer til medlemmet. */
  readonly member: RapportMedlem;
  readonly anchor: TcAnchor;
  readonly spec: TimecodeSpec;
  readonly entries: readonly RapportPost[];
  readonly summary: string;
  readonly naming: ReportNaming;
  /** Klokkeslettet tidskoden sist ble satt, lokal tid. */
  readonly anchorSetAtLocal: string;
  /**
   * SU-/synkspoersmaalene, hvem de gjelder, og hva som ble huket av.
   *
   * Et svar teller bare naar SYNK-POSTEN LEVER, og postene det slaas opp i
   * er de samme som staar i loggen over. Det er grunnen til at lista ikke
   * sendes inn ferdig delt: en egen «stilt»-liste kunne vaert uenig med
   * loggen, og da ville rapporten loeyet.
   */
  /**
   * Produksjonens logo som data-URL, eller null.
   *
   * FERDIG HENTET. `generateReport` er synkron med vilje — den kalles ved
   * skiftslutt og skal ikke kunne henge paa noe — saa fila leses foer,
   * av `state/logolager.ts`.
   *
   * SVG GAAR IKKE. jsPDF tegner PNG, JPEG og WebP, ikke SVG. En
   * SVG-logo hoppes derfor over her, og rapporten faar ingen. Den staar
   * fortsatt i appen og paa nettflata.
   */
  readonly logoDataUrl?: string | null;
  readonly deltakere?: readonly RapportDeltaker[];
  readonly spoersmaal?: readonly RapportSpoersmaal[];
  readonly svar?: readonly RapportSvar[];
  /** Det som er stilt et annet sted. Se RapportStiltEllers. */
  readonly stiltEllers?: readonly RapportStiltEllers[];
}

export interface GeneratedReport {
  readonly blob: Blob;
  readonly filename: string;
}

const MM = { left: 16, right: 16, top: 18, bottom: 18 };
const PAGE_W = 210;
const PAGE_H = 297;
const BODY_W = PAGE_W - MM.left - MM.right;
const TC_COL = 34;
/* Brevhodet. 14 mm hoeyt er omtrent en logo paa et vanlig ark — stort nok
   til aa kjennes igjen, lite nok til aa ikke ta en femtedel av siden. */
const LOGO_H = 14;
const LOGO_W = 45;

/**
 * Rapporten.
 *
 * PDF-en er ikke stilisert. Den leses sittende i godt lys og har ingenting
 * med appens palett å gjøre. Courier er en av PDF-formatets egne skrifter, så
 * den trenger ingen innebygging — og skrivemaskinpreget er riktig for et
 * dokument som ligner et manus.
 *
 * Genereres lokalt. Ingen server er involvert, og nett trengs bare for å
 * sende. Se docs/05-modul-logg.md.
 */
export function generateReport(input: ReportInput): GeneratedReport {
  const { production, member, anchor, spec, entries, summary, naming } = input;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MM.top;

  const ensureRoom = (needed: number): void => {
    if (y + needed <= PAGE_H - MM.bottom) return;
    doc.addPage();
    y = MM.top;
  };

  const line = (text: string, size: number, style: "normal" | "bold" = "normal"): void => {
    doc.setFont("courier", style);
    doc.setFontSize(size);
    const wrapped = doc.splitTextToSize(text, BODY_W) as string[];
    for (const l of wrapped) {
      ensureRoom(size * 0.45);
      doc.text(l, MM.left, y);
      y += size * 0.45;
    }
  };

  /*
   * Logoen oeverst til hoeyre, over tittelen.
   *
   * Hoeyre og ikke venstre: tittelen er det som leses foerst, og en logo
   * foran den ville flyttet lesingen til et merke i stedet for til hvilken
   * dag rapporten gjelder. Den staar bare paa foerste side — det er et
   * brevhode, ikke en vannmerking.
   */
  const logo = input.logoDataUrl ?? null;
  if (logo !== null && !logo.startsWith("data:image/svg")) {
    try {
      const props = doc.getImageProperties(logo) as { width: number; height: number };
      const h = Math.min(LOGO_H, (LOGO_W * props.height) / props.width);
      const w = (h * props.width) / props.height;
      doc.addImage(logo, PAGE_W - MM.right - w, y - 4, w, h);
      /* Tittelen skyves bare ned naar logoen er hoeyere enn den. */
      y += Math.max(0, h - 6);
    } catch {
      /* Et format jsPDF ikke kjente igjen. Rapporten skal ut uansett. */
    }
  }

  line(`Rapport — ${production.name}, opptaksdag ${naming.shootDay}`, 15, "bold");
  y += 3;

  for (const meta of [
    `Enhet: ${naming.unit}   Skift: ${naming.shift}   Dato: ${naming.dateIso}`,
    `Logget av: ${member.displayName}`,
    `Framerate: ${spec.rate.name} fps${spec.dropFrame ? " drop-frame" : ""}`,
    `Tidskode satt ${input.anchorSetAtLocal} til ${formatTimecode(anchor.tcFrames, spec)}`,
    `Rapportversjon: v${naming.version}`,
  ]) {
    line(meta, 9.5);
  }

  y += 4;
  line("Oppsummering", 11, "bold");
  y += 1;
  line(summary.length > 0 ? summary : "—", 10);

  /*
   * ========  SPOERSMAALENE, KOBLET TIL POSTENE  ========
   *
   * Brukeren 03.11.2026: «Hvis et synkspoersmaal blir skrevet til en
   * loggpost, saa er det fint om vi kan highlighte dette i rapporten, slik
   * at man kan linke et synkspoersmaal til en hendelse via rapporten.»
   *
   * Koblingen gaar begge veier i dokumentet: posten i loggen faar
   * spoersmaalet under seg med sin status, og spoersmaalet i SU-delen faar
   * posten det kom fra. Det er dét som gjoer rapporten brukbar for en
   * klipper: tidskoden finnes paa begge sider av lenka.
   */
  const alleSpoersmaal = input.spoersmaal ?? [];
  const alleSvar = input.svar ?? [];
  const ellers = input.stiltEllers ?? [];

  const levendePoster = new Map(
    entries.filter((e) => e.deletedAt === null).map((e) => [e.id, e]));

  /** Spoersmaalene som ble skrevet ut av en post i DENNE loggen. */
  const perPost = new Map<string, RapportSpoersmaal[]>();
  for (const q of alleSpoersmaal) {
    const kilde = q.kilde?.entryId;
    if (kilde === undefined || kilde === "") continue;
    const liste = perPost.get(kilde);
    if (liste === undefined) perPost.set(kilde, [q]); else liste.push(q);
  }

  /** Stilt her, stilt av en annen, eller ikke stilt. */
  type Status =
    | { readonly slag: "her"; readonly frames: number; readonly svar: string }
    | { readonly slag: "andre"; readonly hvem: string; readonly dag: string }
    | { readonly slag: "nei" };

  const herKart = new Map<string, { frames: number; svar: string }>();
  for (const v of alleSvar) {
    const post = levendePoster.get(v.entryId);
    if (post === undefined) continue;
    herKart.set(`${v.spoersmaalId}·${v.deltakerId}`, {
      frames: timecodeAt(anchor, post, spec).frames,
      svar: v.svar,
    });
  }
  const ellersKart = new Map<string, { hvem: string; dag: string }>();
  for (const e of ellers) {
    const noekkel = `${e.spoersmaalId}·${e.deltakerId}`;
    if (herKart.has(noekkel)) continue;
    ellersKart.set(noekkel, { hvem: e.hvem, dag: e.opptaksdag });
  }

  const statusFor = (qId: string, deltakerId: string): Status => {
    const her = herKart.get(`${qId}·${deltakerId}`);
    if (her !== undefined) return { slag: "her", ...her };
    const annen = ellersKart.get(`${qId}·${deltakerId}`);
    if (annen !== undefined) return { slag: "andre", ...annen };
    return { slag: "nei" };
  };

  /**
   * Statusen i én linje, slik den staar under posten i loggen.
   *
   * Et GENERELT spoersmaal gjelder alle, og det er stilt naar det er stilt
   * til noen: lista under posten skal si om hendelsen er fulgt opp, ikke
   * foere regnskap per deltaker — det staar i SU-delen.
   */
  const statusTekst = (q: RapportSpoersmaal): string => {
    const folk = q.deltakerId === null
      ? (input.deltakere ?? []).map((d) => d.id)
      : [q.deltakerId];
    for (const id of folk) {
      const st = statusFor(q.id, id);
      if (st.slag === "her") return `stilt ${formatTimecode(st.frames, spec)}`;
      if (st.slag === "andre") {
        const hvem = st.hvem === "" ? "en annen" : st.hvem;
        return st.dag === "" ? `stilt av ${hvem}` : `stilt av ${hvem}, D${st.dag}`;
      }
    }
    return "ikke stilt ennaa";
  };

  y += 5;
  line("Logg", 11, "bold");
  y += 2;

  // Postene sorteres på veggklokke, ikke på tidskode: med midnattsrullering
  // gir tidskodesortering feil rekkefølge.
  const sorted = entries.slice().sort((a, b) => a.wallUtcMs - b.wallUtcMs);
  let anyUncertain = false;

  doc.setFontSize(10);
  for (const e of sorted) {
    const tc = formatTimecode(timecodeAt(anchor, e, spec).frames, spec);
    const uncertain = e.tcState === "usikker";
    if (uncertain) anyUncertain = true;

    const body = [e.label, e.text].filter((s) => s.length > 0).join("  ");
    doc.setFont("courier", "normal");
    const wrapped = doc.splitTextToSize(body, BODY_W - TC_COL) as string[];

    ensureRoom(Math.max(4.5, wrapped.length * 4.5) + 1.5);
    const startY = y;

    doc.setTextColor(60);
    doc.text(uncertain ? `${tc} *` : tc, MM.left, y);
    doc.setTextColor(0);

    // Markøretiketten står i halvfet, teksten under i vanlig — markører uten
    // tekst er ferdige poster og skal ikke se uferdige ut.
    if (e.label.length > 0 && e.text.length === 0) {
      doc.setFont("courier", "bold");
      doc.text(e.label, MM.left + TC_COL, y);
      y += 4.5;
    } else {
      /*
       * Hver linje sjekker selv om den faar plass.
       *
       * Plassen ble bare sjekket én gang for hele posten. En post lengre enn
       * ei side loep utenfor arket og forsvant fra rapporten uten spor — og
       * rapporten er det redaktoer faar. Fortsetter posten paa neste side,
       * gjentas tidskoden dempet, saa det er tydelig hva linjene hoerer til.
       */
      for (const l of wrapped) {
        if (y > PAGE_H - MM.bottom - 4.5) {
          doc.addPage();
          y = MM.top;
          doc.setTextColor(150);
          doc.setFont("courier", "normal");
          doc.text(`${tc} (forts.)`, MM.left, y);
          doc.setTextColor(0);
        }
        doc.text(l, MM.left + TC_COL, y);
        y += 4.5;
      }
    }

    /*
     * ========  SPOERSMAALET SOM BLE SKREVET UT AV POSTEN  ========
     *
     * Staar under posten, i tidskodespalten sin egen innrykking, saa det
     * leses som en fotnote til hendelsen og ikke som en ny logglinje. Her
     * er det STATUSEN som er poenget: redaktoeren skal se om hendelsen er
     * fulgt opp uten aa bla til SU-delen.
     *
     * ASCII, ingen piler: jsPDFs standardskrifter er WinAnsi, og «→» faller
     * tilbake paa noe annet som tegner hele linja med glipper.
     */
    for (const q of perPost.get(e.id) ?? []) {
      const hvem = q.deltakerId === null
        ? "ALLE"
        : (input.deltakere ?? []).find((d) => d.id === q.deltakerId)?.forkortelse ?? "?";
      doc.setFont("courier", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(90);
      const merke = `SYNK ${hvem}: ${q.tekst}  [${statusTekst(q)}]`;
      for (const l of doc.splitTextToSize(merke, BODY_W - TC_COL) as string[]) {
        if (y > PAGE_H - MM.bottom - 4) { doc.addPage(); y = MM.top; }
        doc.text(l, MM.left + TC_COL, y);
        y += 3.8;
      }
      doc.setTextColor(0);
      doc.setFontSize(10);
      doc.setFont("courier", "normal");
      y += 0.8;
    }

    doc.setDrawColor(225);
    doc.line(MM.left, y - 3, PAGE_W - MM.right, y - 3);
    y = Math.max(y, startY + 4.5) + 1.5;
  }

  /*
   * SJEKKLISTA, DELT I STILT OG USTILT.
   *
   * Det ustilte er halve verdien: redaktoer skal se hva som IKKE ble spurt
   * om, saa det kan tas i neste synk. Derfor staar begge gruppene, og det
   * ustilte er ikke gjemt bak en utelatelse.
   *
   * Generelle spoersmaal gjentas under hver deltaker. De ER stilt til hver
   * enkelt — «Hvordan har du det?» til ALI er ikke det samme som til BEN —
   * og en samlet linje ville skjult hvem som mangler.
   */
  /**
   * Opptaksdagen spoersmaalet ble SKREVET paa.
   *
   * Redaktoer og klipper skal kunne finne materialet spoersmaalet hoerer
   * til, og da holder det ikke aa vite naar det ble stilt: et spoersmaal
   * skrevet paa dag 3 og stilt paa dag 7 peker tilbake paa dag 3.
   * Bestilt 26.10.2026.
   *
   * Tom knagg paa rader fra foer dette fantes. En gjetning er verre enn
   * ingenting: klipperen ville lett i feil materiale.
   */
  const knagg = (q: RapportSpoersmaal): string =>
    q.opptaksdag === null || q.opptaksdag === "" ? "" : `[D${q.opptaksdag}] `;

  /**
   * PU — pickup.
   *
   * Et spoersmaal som har staatt ustilt i over et doegn angaar som regel en
   * tidligere historie som er fulgt opp, og ikke dagen rapporten gjelder.
   * Redaktoer skal se forskjell paa det og det som nettopp ble skrevet,
   * uten aa regne paa datoer. PU er ordet bransjen bruker. Bestilt
   * 26.10.2026.
   *
   * Bare paa det som ikke er stilt: et stilt spoersmaal er aldri en pickup,
   * og de generelle blir det aldri. Regelen ligger i
   * packages/shared/synk — ett sted, saa rapporten og flatene ikke kan
   * vise ulike ting.
   */
  const naaMs = Date.now();
  const pu = (q: RapportSpoersmaal, erStilt: boolean): string =>
    erPickup(
      { skrevetMs: q.skrevetMs, generelt: q.deltakerId === null },
      erStilt,
      naaMs,
    ) ? "[PU] " : "";

  const deltakere = input.deltakere ?? [];
  if (deltakere.length > 0 && alleSpoersmaal.length > 0) {
    const generelle = alleSpoersmaal.filter((q) => q.deltakerId === null);

    y += 5;
    line("SU og synk", 11, "bold");
    y += 1;

    /**
     * Hvor spoersmaalet kom fra, naar det ble skrevet ut av en loggpost.
     *
     * ========  SYNKEN PEKER TILBAKE TIL OPPTAKET  ========
     *
     * Brukeren 03.11.2026: «Naar den som synker har stilt spoersmaalet etter
     * at opptaket spoersmaalet hoerer til [er over], saa skal synk-rapporten
     * referere til opptaks-rapporten som spoersmaalet hoerer til. Dette maa
     * gjoeres tydelig, slik at det er lett aa finne frem.»
     *
     * Tre ting trengs for aa finne innholdet igjen: OPPTAKSDAGEN,
     * TIDSKODEN og RAPPORTEN. De staar paa én linje, og teksten fra posten
     * under — den er det som gjoer at man kjenner igjen hendelsen uten aa
     * hente opp noe annet.
     *
     * Tidskoden regnes av opphavspostens EGET anker, og er alt regnet ut
     * naar spoersmaalet kom ned — se state/su.ts.
     */
    const kildelinje = (q: RapportSpoersmaal): void => {
      const k = q.kilde ?? null;
      if (k === null) return;
      const kspec = timecodeSpec(
        k.framerate as Parameters<typeof timecodeSpec>[0], k.dropFrame,
      );
      const dag = k.opptaksdag === "" ? "" : `opptaksdag ${k.opptaksdag}, `;
      const rap = k.rapportversjon === null
        ? " (ingen rapport levert for den dagen ennaa)"
        : ` — se rapport v${k.rapportversjon}`;
      /* ASCII, og ingen piler. jsPDFs standardskrifter er WinAnsi, og «←»
         finnes ikke der: tegnet falt tilbake til noe annet og hele linja ble
         tegnet med glippe mellom hver bokstav. */
      line(`      Hoerer til ${dag}${formatTimecode(k.tcFrames, kspec)}${rap}`, 8.5);
      if (k.tekst !== "") line(`        «${k.tekst}»`, 8.5);
    };

    for (const d of deltakere) {
      const mine = [...generelle, ...alleSpoersmaal.filter((q) => q.deltakerId === d.id)];
      if (mine.length === 0) continue;

      const her = mine.filter((q) => statusFor(q.id, d.id).slag === "her");
      const andre = mine.filter((q) => statusFor(q.id, d.id).slag === "andre");
      const igjen = mine.filter((q) => statusFor(q.id, d.id).slag === "nei");

      y += 2;
      line(`${d.forkortelse} — ${d.navn}`, 10, "bold");

      if (her.length > 0) {
        line("Stilt:", 9);
        for (const q of her) {
          const st = statusFor(q.id, d.id);
          const t = st.slag === "her" ? st : null;
          line(`  ${formatTimecode(t?.frames ?? 0, spec)}  ${knagg(q)}${q.tekst}`, 9.5);

          /*
           * SVARET UNDER SPOERSMAALET.
           *
           * Skrevet av reporteren eller av regi, oftest i ettertid. Staar det
           * ingenting, staar det ingenting — en tom linje «Svar:» sier bare at
           * noe mangler, og det ser man av at det ikke er der.
           */
          if (t !== null && t.svar !== "") line(`      ${t.svar}`, 9);
          kildelinje(q);
        }
      }

      /*
       * ========  STILT AV EN ANNEN  ========
       *
       * Egen gruppe, ikke en fotnote under «ikke stilt»: for redaktoeren er
       * forskjellen hele poenget. Det er gjort, men ikke her — og da staar
       * svaret i en annen rapport.
       */
      if (andre.length > 0) {
        line("Stilt av andre:", 9);
        for (const q of andre) {
          const st = statusFor(q.id, d.id);
          const hvem = st.slag === "andre" && st.hvem !== "" ? st.hvem : "en annen";
          const dag = st.slag === "andre" && st.dag !== "" ? `, opptaksdag ${st.dag}` : "";
          line(`  ${knagg(q)}${q.tekst}`, 9.5);
          line(`      stilt av ${hvem}${dag}`, 8.5);
          kildelinje(q);
        }
      }

      if (igjen.length > 0) {
        line("Ikke stilt:", 9);
        for (const q of igjen) {
          line(`  ${pu(q, false)}${knagg(q)}${q.tekst}`, 9.5);
          kildelinje(q);
        }
      }
    }
  }

  if (anyUncertain) {
    y += 3;
    doc.setTextColor(90);
    line(
      "* Tidskoden ble regnet fram etter at appen mistet den monotone tellingen. "
      + "Verdien bygger på veggklokka og kan avvike.",
      8.5,
    );
    doc.setTextColor(0);
  }

  return {
    blob: doc.output("blob"),
    filename: `${reportStem(naming)}.pdf`,
  };
}
